import actions from '@src/redux/actions';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Legacy audio player for Dubbing projects: plays the single merged project
 * audio (`audioLinkSrt`) produced by the backend after the whole-project
 * synthesis request.
 *
 * The new per-block create-audio flow (US-14) lives in a separate hook
 * (`useVideoDubbingAudioPlayer`). Both may be mounted at once, so this hook
 * accepts `enabled` (default true): when false, its effects are inert and it
 * neither plays audio nor dispatches, leaving the video hook in control.
 */
export const useAudioPlayerDubbingProject = ({ enabled = true } = {}) => {
  const audioRef = useRef(new Audio());
  const isInitializedRef = useRef(false);
  const isSeekingRef = useRef(false);

  const { audioLinkSrt, audioExpiredAt, isPlaying } = useSelector(
    (state) => state.project,
  );
  const isPlayingRef = useRef(isPlaying);

  const dispatch = useDispatch();
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isErrorPlaying, setIsErrorPlaying] = useState(false);

  // Stable reference for audio operations
  const audioElementRef = useRef(audioRef.current);

  // Initialize audio element once with preload metadata
  useEffect(() => {
    if (!isInitializedRef.current) {
      audioElementRef.current = audioRef.current;
      audioElementRef.current.preload = 'metadata';
      isInitializedRef.current = true;
    }
  }, []);

  // Memoized function to preload audio metadata
  const loadAudioMetadata = useCallback(
    (src) =>
      new Promise((resolve, reject) => {
        const tempAudio = new Audio();
        tempAudio.preload = 'metadata';
        const TIMEOUT_MS = 10 * 1000; // 10 seconds timeout (with 3 retries = 30s total)

        let metadataHandler;
        let errorHandler;
        let timeoutId;

        const cleanupListeners = () => {
          tempAudio.removeEventListener('loadedmetadata', metadataHandler);
          tempAudio.removeEventListener('error', errorHandler);
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
        };

        metadataHandler = () => {
          resolve(tempAudio.duration);
          cleanupListeners();
        };

        errorHandler = () => {
          cleanupListeners();
          // Abort the audio loading to free up resources
          tempAudio.src = '';
          reject(
            new Error(
              `Failed to load audio metadata: ${
                tempAudio.error?.message || 'Unknown error'
              }`,
            ),
          );
        };

        tempAudio.addEventListener('loadedmetadata', metadataHandler);
        tempAudio.addEventListener('error', errorHandler);

        // Add timeout to prevent Promise from hanging forever
        timeoutId = setTimeout(() => {
          cleanupListeners();
          // Abort the audio loading to free up resources
          tempAudio.src = '';
          reject(new Error('Timeout loading audio metadata'));
        }, TIMEOUT_MS);

        // Add cache-busting param to avoid browser caching issues with same URL
        const cacheBustingUrl = src.includes('?')
          ? `${src}&_cb=${Date.now()}`
          : `${src}?_cb=${Date.now()}`;
        tempAudio.src = cacheBustingUrl;
        // Explicitly call load() to ensure metadata loading starts in all browsers
        tempAudio.load();
      }),
    [],
  );

  // Load audio metadata when audioLinkSrt changes
  useEffect(() => {
    if (!enabled) return undefined;
    const audio = audioElementRef.current;

    if (!audioLinkSrt) {
      setDuration(0);
      setCurrentTime(0);
      setIsLoading(false);
      return undefined;
    }

    // Check if audio has expired
    const audioHasExpired = audioExpiredAt && moment().isAfter(audioExpiredAt);
    if (audioHasExpired) {
      setDuration(0);
      setCurrentTime(0);
      setIsLoading(false);
      return undefined;
    }

    // Preload metadata using separate audio instance
    setIsLoading(true);
    setIsErrorPlaying(false);

    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 1000;

    const loadMetadata = async () => {
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const durationMs = await loadAudioMetadata(audioLinkSrt);

          setDuration(durationMs);
          setIsLoading(false);

          // Set the source for the main audio element
          audio.src = audioLinkSrt;
          return; // Success - exit the retry loop
        } catch (error) {
          const isTimeoutError = error?.message?.includes('Timeout');

          // Only retry for timeout errors, not for actual media errors (like 404)
          if (!isTimeoutError || attempt >= MAX_RETRIES) {
            break; // Don't retry for non-timeout errors or if max retries reached
          }

          // Wait before retrying
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            setTimeout(resolve, RETRY_DELAY_MS);
          });
        }
      }

      // All retries failed
      setIsErrorPlaying(true);
      setIsLoading(false);
      setDuration(0);
    };

    loadMetadata();

    return undefined;
  }, [enabled, audioLinkSrt, audioExpiredAt, loadAudioMetadata]);

  // Handle time update
  const handleTimeUpdate = useCallback(() => {
    // Don't update time if user is seeking to avoid jitter
    if (!isSeekingRef.current) {
      const time = audioElementRef.current.currentTime;
      setCurrentTime(time);
    }
  }, []);

  // Add event listeners (only while this engine is active, so a disabled
  // instance attaches nothing and stays fully inert).
  useEffect(() => {
    if (!enabled) return undefined;
    const audio = audioElementRef.current;
    const handleEnded = () => {
      dispatch(actions.project.setIsPlaying(false));
      setCurrentTime(0);
      audioElementRef.current.currentTime = 0;
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [enabled, handleTimeUpdate, dispatch]);

  // Keep isPlayingRef in sync
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Play/pause based on isPlaying state
  useEffect(() => {
    if (!enabled) return;
    const audio = audioElementRef.current;
    if (isPlaying) {
      audio.play().catch((error) => {
        const ERROR_DUE_TO_EXPIRED_AUDIO_LINK = 'NotSupportedError';
        if (error.name === ERROR_DUE_TO_EXPIRED_AUDIO_LINK) {
          setIsErrorPlaying(true);
        }
      });
    } else {
      audio.pause();
    }
  }, [enabled, isPlaying]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      const audio = audioElementRef.current;
      audio.pause();
      audio.currentTime = 0;
    },
    [],
  );

  const handleChangeVolume = useCallback((value) => {
    audioElementRef.current.volume = value / 100;
  }, []);

  const handlePlay = useCallback(() => {
    // Check if audio has expired
    const audioHasExpired = audioExpiredAt && moment().isAfter(audioExpiredAt);
    if (audioHasExpired || !audioLinkSrt) {
      return;
    }

    dispatch(actions.project.setIsPlaying(true));
  }, [audioLinkSrt, audioExpiredAt, dispatch]);

  const handlePause = useCallback(() => {
    dispatch(actions.project.setIsPlaying(false));
  }, [dispatch]);

  const handleSeeked = useCallback((value) => {
    const timeInSeconds = value;
    const audio = audioElementRef.current;

    isSeekingRef.current = true;

    // Only seek if audio source is loaded
    if (audio.src && audio.readyState >= 1) {
      audio.currentTime = timeInSeconds;
      setCurrentTime(value);
    }

    // Reset seeking flag after a short delay
    setTimeout(() => {
      isSeekingRef.current = false;
    }, 100);
  }, []);

  // Exact position straight off the element, read without a re-render — lets the
  // playhead follow playback per animation frame.
  const getCurrentSec = useCallback(
    () => audioElementRef.current?.currentTime ?? 0,
    [],
  );

  return {
    audioRef,
    handlePlay,
    handlePause,
    handleSeeked,
    getCurrentSec,
    isLoading,
    isErrorPlaying,
    isPlaying,
    currentTime,
    duration,
    handleChangeVolume,
  };
};

export default useAudioPlayerDubbingProject;
